import React from 'react';

// --- Helper for formatting numbers ---
const formatCurrency = (val) => `₹${val ?? 0}`;
const formatPercent = (val) => `${val ?? 0}%`;

/* =======================================
   1. DAY BY DAY TABLE (The logic you requested)
   ======================================= */
export const DayByDayComparisonTable = ({ data }) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  // 1. Extract the "Difference" summary (usually the last item)
  const differenceRow = data.find(item => item.period_type === "difference");
  
  // 2. Group the rest by Day of Week (dow)
  const groupedByDay = data.reduce((acc, item) => {
    if (item.period_type === "difference") return acc;
    
    if (!acc[item.dow]) acc[item.dow] = [];
    acc[item.dow].push(item);
    return acc;
  }, {});

  return (
    <div className="w-full space-y-6">
      <h3 className="font-bold text-gray-700 text-lg">Day-by-Day Comparison</h3>

      {/* Render a small table for each Day */}
      {Object.entries(groupedByDay).map(([day, rows], index) => (
        <div key={index} className="border rounded-xl shadow-sm overflow-hidden bg-white">
          {/* Header for the Day */}
          <div className="bg-[#2e38b5] text-white px-4 py-2 font-semibold flex justify-between">
            <span>{day}</span>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-gray-100 text-gray-600 border-b">
              <tr>
                <th className="px-4 py-2 text-left">Period</th>
                <th className="px-4 py-2 text-left">Date</th>
                <th className="px-4 py-2 text-right">Impr.</th>
                <th className="px-4 py-2 text-right">Clicks</th>
                <th className="px-4 py-2 text-right">CTR</th>
                <th className="px-4 py-2 text-right">Cost</th>
                <th className="px-4 py-2 text-right">Conv.</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium capitalize text-blue-600">
                    {row.period_type}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{row.date}</td>
                  <td className="px-4 py-3 text-right">{row.impressions}</td>
                  <td className="px-4 py-3 text-right">{row.clicks}</td>
                  <td className="px-4 py-3 text-right">{formatPercent(row.ctr)}</td>
                  <td className="px-4 py-3 text-right">{formatCurrency(row.cost)}</td>
                  <td className="px-4 py-3 text-right">{row.conversions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {/* Optional: Render the Difference Summary at the bottom */}
      {differenceRow && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="font-bold text-green-800 mb-2">Total Change (Difference)</p>
          <div className="flex gap-4 text-sm text-green-700 flex-wrap">
            <span>Impr: {differenceRow.impressions}</span>
            <span>Clicks: {differenceRow.clicks}</span>
            <span>Cost: {formatCurrency(differenceRow.cost)}</span>
            <span>Conv: {differenceRow.conversions}</span>
          </div>
        </div>
      )}
    </div>
  );
};

/* =======================================
   2. OVERALL TABLE (Your existing view)
   ======================================= */
export const ComparisonSummaryTable = ({ data }) => {
  // Logic for the screenshot you provided
  const uniqueRows = data.reduce((acc, current) => {
    const x = acc.find(item => item.period_type === current.period_type);
    if (!x) {
      return acc.concat([current]);
    } else {
      return acc;
    }
  }, []);

  return (
    <div className="w-full bg-white border rounded-xl shadow-sm overflow-hidden">
       <table className="w-full text-sm">
        <thead className="bg-[#2e38b5] text-white">
          <tr>
            <th className="px-4 py-3 text-left">Period</th>
            <th className="px-4 py-3 text-right">Impressions</th>
            <th className="px-4 py-3 text-right">Clicks</th>
            <th className="px-4 py-3 text-right">CTR</th>
            <th className="px-4 py-3 text-right">Cost</th>
            <th className="px-4 py-3 text-right">Conversions</th>
          </tr>
        </thead>
        <tbody>
          {uniqueRows.map((row, i) => (
             <tr key={i} className={`border-b ${row.period_type === 'difference' ? 'bg-green-50 font-bold' : ''}`}>
                <td className="px-4 py-3 capitalize">{row.period_type === 'current' ? 'This Week' : row.period_type === 'previous' ? 'Last Week' : 'Change'}</td>
                <td className="px-4 py-3 text-right">{row.impressions}</td>
                <td className="px-4 py-3 text-right">{row.clicks}</td>
                <td className="px-4 py-3 text-right">{formatPercent(row.ctr)}</td>
                <td className="px-4 py-3 text-right">{formatCurrency(row.cost)}</td>
                <td className="px-4 py-3 text-right">{row.conversions}</td>
             </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};